// Ported from the retired sticky_offers.js: a filter, sort or tag change lifts the
// results column to the top of the viewport without dragging the filters column
// along, and only snaps the window back to the search panel once the two columns
// stand level again.
const COLUMN_ALIGNMENT_TOLERANCE = 5
const PANEL_FIXED_CLASS = "body_place-search-inner-fixed"

stimulusApp.register(
  "sticky-offers",
  class extends Stimulus.Controller {
    static classes = ["fixed"]
    static targets = ["column", "marker", "placeholder"]

    #state = null
    #tracking = false
    #provisional = false
    #previousScroll = 0
    #topInset = 0

    // A filter change is a stream rather than a visit, and a stream fires no
    // `turbo:visit`. Measured here, before the request, the column is still the
    // outgoing one. Keep in sync with `filter_tag_link_data` and
    // `filters/_form`: `replace` marks a submission as a filter change, and
    // Turbo copies it from a stream link — a tag's ✕, a sort option, a reset —
    // onto the hidden form it submits from `<body>`, which is why the listener
    // sits on the document. A heal only resolves the stay counts, so it must
    // not move the reader.
    start(event) {
      if (event.target.dataset.turboAction !== "replace") return
      if (event.detail.formSubmission.body.has("sidebar_only")) return

      this.#start()
    }

    #start() {
      if (!this.hasColumnTarget || !this.#panelFixed) return

      this.#topInset = parseFloat(
        getComputedStyle(this.columnTarget).getPropertyValue(
          "--search-results--fixed-top",
        ),
      )
      this.#previousScroll = window.scrollY
      this.#tracking = true
      this.#provisional = true
      this.#lift(this.#topInset - this.#top(this.element))

      if (this.#settled) this.#finish()
    }

    // Keep in sync with desktop/place_search_inner.js and the matching `data-action`:
    // it announces the reserved-height writes that move `.content` under us.
    realign() {
      if (!this.#provisional || this.#state !== "lifted") return

      this.#lift(this.#topInset - this.#top(this.element))
    }

    // Deferred a frame: the search panel's own scroll listener settles
    // `body_place-search-inner-fixed` in a rAF of its own, and #settled reads it.
    reposition() {
      this.#provisional = false
      requestAnimationFrame(() => this.#reposition())
    }

    #reposition() {
      if (!this.#tracking) return

      if (this.#settled) {
        this.#finish()
        return
      }

      const scroll = window.scrollY
      const scrollingDown = scroll > this.#previousScroll
      this.#previousScroll = scroll

      if (scrollingDown) {
        if (this.#state === "pinned") this.#lift()
      } else if (
        this.#state === "lifted" &&
        this.#topVisible(this.columnTarget)
      ) {
        this.#pin()
      }
    }

    columnTargetDisconnected(element) {
      this.#tracking = false
      this.#state = null
      element.style.marginTop = ""
      element.style.left = ""
      element.classList.remove(this.fixedClass)
      this.placeholderTarget.style.height = ""
    }

    // The column rides the page on a margin, so it keeps its place in the flow and
    // the document keeps its height.
    #lift(offset) {
      const resolved =
        offset ??
        Math.abs(this.#top(this.element) - this.#top(this.columnTarget))

      this.#state = "lifted"
      this.#release()
      this.columnTarget.style.marginTop = `${resolved}px`
    }

    // Out of the flow the column no longer holds the row open, so the placeholder
    // takes over the height it was contributing.
    #pin() {
      const wrapper = this.element.getBoundingClientRect()
      const column = this.columnTarget.getBoundingClientRect()
      const overlap = wrapper.bottom - column.top

      this.#state = "pinned"
      this.placeholderTarget.style.height = `${wrapper.height + column.height - overlap}px`
      this.columnTarget.style.marginTop = ""
      this.columnTarget.style.left = `${column.left + window.scrollX}px`
      this.columnTarget.classList.add(this.fixedClass)
    }

    #release() {
      this.placeholderTarget.style.height = ""
      this.columnTarget.style.left = ""
      this.columnTarget.classList.remove(this.fixedClass)
    }

    // Pinning first keeps the column still while the window travels underneath it;
    // releasing afterwards lands it exactly where the flow already puts it.
    #finish() {
      this.#tracking = false
      this.#pin()
      this.#state = null
      this.#scrollToPanel()
    }

    // 1px short: sub-pixel scaling can leave the scroll a hair below the target.
    // The browser can also restore the offset on the next frame, so re-check once
    // it has settled and try again.
    #scrollToPanel() {
      const top = this.#panelTop

      document.documentElement.scrollTop = top - 1
      requestAnimationFrame(() => {
        this.#release()
        requestAnimationFrame(() => {
          if (document.documentElement.scrollTop > top) this.#scrollToPanel()
        })
      })
    }

    // Measured off the element in front of the panel: the panel is `position: fixed`
    // while the user is scrolled down, so its own box is out of the flow.
    get #panelTop() {
      const previous = document.querySelector(
        ".place-search-inner",
      ).previousElementSibling
      const margin = parseFloat(getComputedStyle(previous).marginBottom)

      return window.scrollY + previous.getBoundingClientRect().bottom + margin
    }

    get #settled() {
      if (!this.#panelFixed) return true

      const distance = Math.abs(
        this.#top(this.markerTarget) - this.#top(this.columnTarget),
      )

      return (
        this.#topVisible(this.markerTarget) &&
        distance <= COLUMN_ALIGNMENT_TOLERANCE
      )
    }

    get #panelFixed() {
      return document.body.classList.contains(PANEL_FIXED_CLASS)
    }

    #topVisible(element) {
      return this.#top(element) >= this.#topInset
    }

    #top(element) {
      return element.getBoundingClientRect().top
    }
  },
)
